import React, { useContext, useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch-dom';
import { useHistory, useParams } from 'react-router-dom';

import { Loader } from '../../../../components/Loader';
import { PreloadImageLink } from '../../../../components/PreloadImageLink';
import RouteWithCode from '../../../../components/RouteWithCode/RouteWithCode';
import { SeoBlock } from '../../../../components/SeoBlock';
import { RM_TAGS } from '../../../../const';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import { useRmTag } from '../../../../context/RmContext';
import { GET_EXPERT_BY_SLUG } from '../../../../graphql/user';
import { Tenant, LearnerInfo, Query, QueryGetFilteredUsersArgs } from '../../../../types';
import { generateSeoTitle, translate } from '../../../../utils/helpers';
import { useClearSearchState } from '../../../../utils/hooks';
import NotFoundPage from '../../../NotFoundPage';
import Breadcrumbs from '../../components/Breadcrumbs';
import { ExpertInfo } from '../../components/ExpertInfo';
import { Header } from '../../components/Header';
import { useCommonHeaderStyles } from '../../components/Header/style';
import SERPContainer from '../../components/SERPContainer/SERPContainer';
import { Search } from '../../components/Search';
import { Tabs } from '../../components/Tabs';
import { DEFAULT_URL } from '../../const';
import useStyles from './styles';

const getNavigationArray = (expertName) => {
  return [
    {
      title: translate('page.discover.discover'),
      url: DEFAULT_URL,
    },
    {
      title: translate('page.discover.experts'),
      url: `/experts`,
    },
    {
      title: expertName,
    },
  ];
};

const generateExpertSrcset = (height?) => {
  const locHeight = height < 580 ? 580 : height;
  return [
    { width: 500, quality: 80, focus: 'auto', height: locHeight },
    { width: 800, quality: 80, focus: 'auto', height: locHeight },
    { width: 1100, quality: 80, focus: 'auto', height: locHeight },
    { width: 1200, quality: 80, focus: 'auto', height: locHeight },
    { width: 1920, quality: 80, focus: 'auto', height: locHeight },
  ];
};

interface ExpertPageProps {
  tenant: Tenant;
  algoliaIndexName: string;
  searchClient: any;
  userInfo?: LearnerInfo;
  loadingKey: boolean;
  logoUrl?: string;
}

const ExpertsPage: React.FC<ExpertPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  userInfo,
  loadingKey,
  logoUrl,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const headerClasses = useCommonHeaderStyles();
  const headerRef = useRef<HTMLDivElement | null>(null);

  const history = useHistory();
  const { expertSlug } = useParams();
  const { searchState, setSearchState } = useContext(DiscoverContext);

  //clear search state on page change
  useClearSearchState(true);
  useRmTag(RM_TAGS.ExpertPage);

  const { data, loading } = useQuery<Pick<Query, 'getFilteredUsers'>, QueryGetFilteredUsersArgs>(
    GET_EXPERT_BY_SLUG,
    {
      variables: {
        filter: {
          slug: expertSlug,
        },
      },
    },
  );

  const expert = data?.getFilteredUsers?.length ? data?.getFilteredUsers[0] : null;

  const navigationArray = getNavigationArray(expert?.name);

  const imgSrcset = generateExpertSrcset(headerRef?.current?.clientHeight);

  const handleSearch = (val) => {
    setSearchState((prevState) => ({
      ...prevState,
      query: val,
    }));
    history.push(`${DEFAULT_URL}/search`);
  };

  //set expert filter
  useEffect(() => {
    if (expert?.name) {
      setSearchState((prevState) => ({
        ...prevState,
        refinementList: {
          ...prevState?.refinementList,
          user: [expert.name],
        },
      }));
    }
  }, [expert, setSearchState]);

  if (loading || loadingKey) {
    return (
      <>
        {expert && (
          <>
            <SeoBlock
              data={{
                seo_title: generateSeoTitle(expert?.name, tenant?.title),
                seo_description: expert?.description || '',
                share_image: expert?.avatar,
              }}
              canonicalUrl={`/experts/${expertSlug}`}
            />
            <PreloadImageLink path={expert?.avatar} srcset={imgSrcset} />
          </>
        )}
        <Loader />
      </>
    );
  }

  if (!expert) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  return (
    <>
      <SeoBlock
        data={{
          seo_title: generateSeoTitle(expert?.name, tenant?.title),
          seo_description: expert?.description || '',
          share_image: expert?.avatar,
        }}
        canonicalUrl={`${DEFAULT_URL}/experts/${expertSlug}`}
      />
      <PreloadImageLink path={expert?.avatar} srcset={imgSrcset} />
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        searchClient={searchClient}
        indexName={algoliaIndexName}
      >
        <Header
          ref={headerRef}
          title={t('page.discover.discover')}
          imageUrl={expert?.avatar || undefined}
          wrapperClass={classes.headerWrapper}
          contentWrapperClass={classes.headerContentWrapper}
          imageSrcset={imgSrcset}
          imageWrapperClass={classes.imageWrapper}
          tenantName={tenant?.name}
        >
          <Container className={headerClasses.centeredContainer}>
            <Search wrapperClass={headerClasses.search} refine={handleSearch} withClear />
            <Tabs />
          </Container>
          <Container className={classes.headerInfoContainer}>
            <Breadcrumbs navigation={navigationArray} />
            <ExpertInfo
              name={expert?.name}
              jobTitle={expert?.job_title}
              description={expert?.description}
            />
          </Container>
        </Header>
        <Container>
          <div className={classes.expertInfoContainer}>
            <Breadcrumbs navigation={navigationArray} />
            <Box className={headerClasses.wrapperWithBorder}>
              <ExpertInfo
                name={expert?.name}
                jobTitle={expert?.job_title}
                description={expert?.description}
              />
            </Box>
          </div>
          <SERPContainer
            logoUrl={logoUrl}
            userInfo={userInfo}
            tenantTitle={tenant?.title}
            tenantName={tenant?.name}
            linkedInId={tenant?.linkedin_id}
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default ExpertsPage;
